import axios, { AxiosError } from "axios";
import { auth } from "../firebase";

type ApiError = { message?: string };

const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

export type RetentionPreferences = {
  businessId: number;
  userId: number;
  weeklySummaryEnabled: boolean;
  milestoneAlertsEnabled: boolean;
  inactivityNudgesEnabled: boolean;
  recommendationEmailsEnabled: boolean;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  unsubscribedAt: string | null;
  timezone: string;
  sendDayOfWeek: number;
  sendHour: number;
  updatedAt?: string;
};

export type RetentionPreferencePatch = Partial<
  Pick<
    RetentionPreferences,
    | "emailEnabled"
    | "weeklySummaryEnabled"
    | "inactivityNudgesEnabled"
    | "milestoneAlertsEnabled"
    | "recommendationEmailsEnabled"
    | "timezone"
    | "sendDayOfWeek"
    | "sendHour"
  >
>;

export async function get_retention_preferences(businessId: number) {
  try {
    const headers = await authHeaders();
    return backendAPI.get<RetentionPreferences>(
      `/retention/business/${businessId}/preferences`,
      { headers }
    );
  } catch (error) {
    const err = error as AxiosError<ApiError>;
    throw new Error(err.response?.data?.message || "Failed to load retention preferences");
  }
}

export async function update_retention_preferences(
  businessId: number,
  patch: RetentionPreferencePatch
) {
  try {
    const headers = await authHeaders();
    return backendAPI.patch<RetentionPreferences>(
      `/retention/business/${businessId}/preferences`,
      patch,
      { headers }
    );
  } catch (error) {
    const err = error as AxiosError<ApiError>;
    throw new Error(err.response?.data?.message || "Failed to update retention preferences");
  }
}

export async function resubscribe_retention_preferences(
  businessId: number,
  patch: RetentionPreferencePatch = {}
) {
  try {
    const headers = await authHeaders();
    return backendAPI.post<RetentionPreferences>(
      `/retention/business/${businessId}/preferences/resubscribe`,
      patch,
      { headers }
    );
  } catch (error) {
    const err = error as AxiosError<ApiError>;
    throw new Error(err.response?.data?.message || "Failed to resubscribe retention emails");
  }
}

export type RetentionUnsubscribeResponse = {
  success: boolean;
  reason?: string | null;
  businessId?: number;
  message?: string;
};

export async function unsubscribe_retention_email(token: string) {
  try {
    return backendAPI.post<RetentionUnsubscribeResponse>("/retention/unsubscribe", { token });
  } catch (error) {
    const err = error as AxiosError<RetentionUnsubscribeResponse & ApiError>;
    throw new Error(
      err.response?.data?.message || "Unable to process unsubscribe request"
    );
  }
}
